import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

export const WastedAdSpendChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance) return [];

        const adItems = (finance.detailedBreakdown || finance.breakdown || []).filter(
            (item: any) => ['advertising', 'ad_spend', 'marketing'].includes(item.category)
        );

        const spendByEntity: Record<string, { name: string; spend: number }> = {};
        const revenueEntityIds = new Set<string>();

        for (const item of adItems) {
            const entityId = item.entityId as string;
            const value = Math.abs(Number(item.value) || 0);

            if (item.type === 'EXPENSE') {
                if (!spendByEntity[entityId]) {
                    spendByEntity[entityId] = {
                        name: (item.entityName as string) || entityId,
                        spend: 0,
                    };
                }
                spendByEntity[entityId].spend += value;
            } else if (item.type === 'REVENUE' && value > 0) {
                revenueEntityIds.add(entityId);
            }
        }

        const wasted = Object.entries(spendByEntity)
            .filter(([entityId]) => !revenueEntityIds.has(entityId))
            .map(([, data]) => ({
                name: data.name,
                spend: data.spend,
            }))
            .sort((a, b) => b.spend - a.spend)
            .slice(0, 10);

        return wasted;
    }, [finance?.breakdown]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={chartData}
                        layout="vertical"
                        margin={{ top: 10, right: 20, bottom: 10, left: 80 }}
                    >
                        <CartesianGrid
                            horizontal={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            type="number"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            tickFormatter={(value: number) => formatCompactNumber(value, currencySymbol)}
                        />
                        <YAxis
                            type="category"
                            dataKey="name"
                            axisLine={CHART_STYLES.axis.axisLine}
                            tickLine={CHART_STYLES.axis.tickLine}
                            tick={CHART_STYLES.axis.tick}
                            width={75}
                        />
                        <Tooltip
                            contentStyle={CHART_STYLES.tooltip.contentStyle}
                            cursor={CHART_STYLES.tooltip.cursor}
                            formatter={(value: number) => [
                                formatCompactNumber(value, currencySymbol),
                                t('charts.wastedSpend', 'Wasted Spend'),
                            ]}
                        />
                        <Bar
                            dataKey="spend"
                            fill={CHART_COLORS.danger}
                            radius={[0, 3, 3, 0]}
                            maxBarSize={CHART_STYLES.bar.maxBarSize}
                        />
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
